import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from "amazon-chime-sdk-js";

import { useVideoCallResilience } from "../../../components/InlineVideoCall/useVideoCallResilience";
import { checkWebRTCSupport } from "../../../components/InlineVideoCall/videoCallUtils";
import styles from "./VideoCallPage.module.css";

const VideoCallPage = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [error, setError] = useState(null);
  const [, setAudioPermissionGranted] = useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [availableAudioInputs, setAvailableAudioInputs] = useState([]);
  const [availableVideoInputs, setAvailableVideoInputs] = useState([]);
  const [availableAudioOutputs, setAvailableAudioOutputs] = useState([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState(null);
  const [selectedVideoInput, setSelectedVideoInput] = useState(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(null);
  const [remoteSpeakerLevels, setRemoteSpeakerLevels] = useState({});
  const [remoteParticipantMuted, setRemoteParticipantMuted] = useState(false);
  const [needsAudioUnmute, setNeedsAudioUnmute] = useState(false);
  const [audioElementState, setAudioElementState] = useState({
    hasStream: false,
    isPlaying: false,
    currentTime: 0,
    readyState: 0,
  });
  const [remoteAudioActive, setRemoteAudioActive] = useState(false);
  const [audioVisualizerData, setAudioVisualizerData] = useState(
    new Array(16).fill(0)
  );

  // Resilience hook for automatic reconnection and monitoring
  const {
    setupMeetingResilience,
    createResilientObserver,
    bindAudioWithResilience,
    handleRemoteAttendeeLeft,
    cleanup: cleanupResilience,
  } = useVideoCallResilience({
    onReconnecting: (attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
    },
    onReconnected: () => {
      console.log("Reconnected successfully");
      setIsConnected(true);
    },
    onReconnectionFailed: () => {
      console.error("All reconnection attempts failed");
      setError("Connection lost. Please rejoin the meeting.");
    },
    onNetworkQualityChange: (quality) => {
      console.log("Network quality changed:", quality);
    },
    onAudioRecovered: () => {
      console.log("Audio recovered");
    },
    onDeviceChanged: async (devices) => {
      setAvailableAudioInputs(devices.audioInputs || []);
      setAvailableVideoInputs(devices.videoInputs || []);
      setAvailableAudioOutputs(devices.audioOutputs || []);
    },
  });

  const meetingSessionRef = useRef(null);
  // Chime's removeObserver() only removes the object it is given, so keep the
  // ones added; otherwise they keep firing through teardown (KIN-1987).
  const audioVideoObserversRef = useRef([]);
  const audioElementRef = useRef(null);
  const videoElementRef = useRef(null);
  const remoteVideoElementRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const shownToastsRef = useRef(new Set());

  const showToastOnce = (id, message, type = "success") => {
    if (shownToastsRef.current.has(id)) {
      return;
    }
    shownToastsRef.current.add(id);
    if (type === "success") {
      toast.success(message, { id });
    } else if (type === "error") {
      toast.error(message, { id });
    }
  };

  const STORAGE_KEYS = {
    audioInput: "kinesin_preferred_audio_input",
    videoInput: "kinesin_preferred_video_input",
    audioOutput: "kinesin_preferred_audio_output",
  };

  const saveDevicePreference = (key, deviceId) => {
    try {
      localStorage.setItem(key, deviceId);
    } catch (e) {
      console.warn("Failed to save device preference:", e);
    }
  };

  const getSavedDevicePreference = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn("Failed to get device preference:", e);
      return null;
    }
  };

  const findSavedDevice = (devices, savedDeviceId) => {
    if (!savedDeviceId || !devices || devices.length === 0) return null;
    return devices.find((d) => d.deviceId === savedDeviceId) || null;
  };

  const cleanupMeeting = async () => {
    // Cleanup resilience monitors first
    cleanupResilience();

    if (meetingSessionRef.current) {
      try {
        meetingSessionRef.current.audioVideo.stopLocalVideoTile();

        await meetingSessionRef.current.audioVideo.stopAudioInput();

        await meetingSessionRef.current.audioVideo.stopVideoInput();

        meetingSessionRef.current.audioVideo.realtimeUnsubscribeFromVolumeIndicator(
          meetingSessionRef.current.configuration.credentials.attendeeId
        );

        audioVideoObserversRef.current.forEach((observer) =>
          meetingSessionRef.current.audioVideo.removeObserver(observer)
        );
        audioVideoObserversRef.current = [];

        meetingSessionRef.current.audioVideo.stop();

        setIsMuted(false);
        setIsVideoOff(false);
        setMicrophoneLevel(0);
        setIsConnected(false);
        setAudioPermissionGranted(false);
      } catch (err) {
        console.error("Error during meeting cleanup:", err);
      }
    }

    localStorage.removeItem("videoCallCredentials");
  };

  const checkMicrophonePermissions = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari."
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error(
          "No microphone tracks available. Please check your microphone connection."
        );
      }

      stream.getTracks().forEach((track) => track.stop());
      setAudioPermissionGranted(true);
      return true;
    } catch (error) {
      console.error("Microphone permission check failed:", error);
      setAudioPermissionGranted(false);

      if (error.name === "NotAllowedError") {
        throw new Error(
          "Microphone access denied. Please click the microphone icon in your browser's address bar and allow microphone access, then refresh the page."
        );
      } else if (error.name === "NotFoundError") {
        throw new Error(
          "No microphone found. Please connect a microphone to your device and try again."
        );
      } else if (error.name === "NotReadableError") {
        throw new Error(
          "Microphone is already in use by another application. Please close other applications using the microphone and try again."
        );
      } else if (error.name === "OverconstrainedError") {
        throw new Error(
          "No suitable microphone found. Please check your microphone settings."
        );
      } else if (error.name === "AbortError") {
        throw new Error("Microphone access was interrupted. Please try again.");
      } else {
        throw new Error(`Microphone error: ${error.message}`);
      }
    }
  };

  const verifyAudioTransmission = async () => {
    const audioElement = audioElementRef.current;
    if (!audioElement) {
      console.warn("⚠️ Audio element not found");
      return;
    }

    const monitorAudioElement = () => {
      const state = {
        hasStream: !!audioElement.srcObject,
        isPlaying: !audioElement.paused,
        currentTime: audioElement.currentTime,
        readyState: audioElement.readyState,
        volume: audioElement.volume,
        muted: audioElement.muted,
      };

      setAudioElementState(state);

      if (state.hasStream) {
        const stream = audioElement.srcObject;
        if (stream && stream.getAudioTracks) {
          stream.getAudioTracks();
        }
      }
    };

    const setupAudioVisualizer = () => {
      try {
        if (audioContextRef.current) {
          return;
        }

        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        if (audioElement.srcObject) {
          const source = audioContext.createMediaStreamSource(
            audioElement.srcObject
          );
          source.connect(analyser);
        } else {
          try {
            const source = audioContext.createMediaElementSource(audioElement);
            source.connect(analyser);
          } catch (e) {}
        }

        const updateVisualizer = () => {
          if (!analyserRef.current) return;

          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount
          );
          analyserRef.current.getByteFrequencyData(dataArray);

          const normalizedData = Array.from(dataArray.slice(0, 16)).map((v) =>
            Math.round((v / 255) * 100)
          );
          setAudioVisualizerData(normalizedData);

          const hasAudio = normalizedData.some((v) => v > 5);
          setRemoteAudioActive(hasAudio);

          animationFrameRef.current = requestAnimationFrame(updateVisualizer);
        };

        updateVisualizer();
      } catch (error) {
        console.error("❌ Error setting up audio visualizer:", error);
      }
    };

    setTimeout(() => {
      monitorAudioElement();
      setupAudioVisualizer();
    }, 2000);

    const monitorInterval = setInterval(monitorAudioElement, 5000);

    audioElement.addEventListener("playing", () => {
      setAudioElementState((prev) => ({ ...prev, isPlaying: true }));
      setupAudioVisualizer();
    });

    audioElement.addEventListener("pause", () => {
      setAudioElementState((prev) => ({ ...prev, isPlaying: false }));
    });

    audioElement.addEventListener("loadeddata", () => {
      setupAudioVisualizer();
    });

    return () => {
      clearInterval(monitorInterval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  };

  const loadAvailableDevices = async (meetingSession) => {
    try {
      const audioInputDevices =
        await meetingSession.audioVideo.listAudioInputDevices();
      const videoInputDevices =
        await meetingSession.audioVideo.listVideoInputDevices();
      const audioOutputDevices =
        await meetingSession.audioVideo.listAudioOutputDevices();

      if (audioInputDevices.length > 0) {
        setAvailableAudioInputs(audioInputDevices);
      }
      if (videoInputDevices.length > 0) {
        setAvailableVideoInputs(videoInputDevices);
      }
      if (audioOutputDevices.length > 0) {
        setAvailableAudioOutputs(audioOutputDevices);
      }

      const savedAudioInputId = getSavedDevicePreference(
        STORAGE_KEYS.audioInput
      );
      const savedVideoInputId = getSavedDevicePreference(
        STORAGE_KEYS.videoInput
      );
      const savedAudioOutputId = getSavedDevicePreference(
        STORAGE_KEYS.audioOutput
      );

      if (audioInputDevices.length > 0 && !selectedAudioInput) {
        const savedDevice = findSavedDevice(
          audioInputDevices,
          savedAudioInputId
        );
        if (savedDevice) {
          setSelectedAudioInput(savedDevice);
        } else {
          setSelectedAudioInput(audioInputDevices[0]);
        }
      }
      if (videoInputDevices.length > 0 && !selectedVideoInput) {
        const savedDevice = findSavedDevice(
          videoInputDevices,
          savedVideoInputId
        );
        if (savedDevice) {
          setSelectedVideoInput(savedDevice);
        } else {
          setSelectedVideoInput(videoInputDevices[0]);
        }
      }
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (!isIOS && audioOutputDevices.length > 0 && !selectedAudioOutput) {
        const savedDevice = findSavedDevice(
          audioOutputDevices,
          savedAudioOutputId
        );
        if (savedDevice) {
          setSelectedAudioOutput(savedDevice);
        } else {
          setSelectedAudioOutput(audioOutputDevices[0]);
        }
      }
    } catch (error) {
      console.error("Failed to load devices:", error);
    }
  };

  const changeAudioInputDevice = async (device) => {
    if (!meetingSessionRef.current) return;

    try {
      await meetingSessionRef.current.audioVideo.startAudioInput(
        device.deviceId
      );
      setSelectedAudioInput(device);
      saveDevicePreference(STORAGE_KEYS.audioInput, device.deviceId);
      toast.success(`Switched to microphone: ${device.label || "Default"}`);
    } catch (error) {
      console.error("Failed to change audio device:", error);
      toast.error("Failed to change microphone");
    }
  };

  const changeVideoInputDevice = async (device) => {
    if (!meetingSessionRef.current) return;

    try {
      await meetingSessionRef.current.audioVideo.startVideoInput(
        device.deviceId
      );
      setSelectedVideoInput(device);
      saveDevicePreference(STORAGE_KEYS.videoInput, device.deviceId);
      toast.success(`Switched to camera: ${device.label || "Default"}`);
    } catch (error) {
      console.error("Failed to change video device:", error);
      toast.error("Failed to change camera");
    }
  };

  const changeAudioOutputDevice = async (device) => {
    if (!meetingSessionRef.current) return;

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      toast.error("Speaker selection is not supported on this device");
      return;
    }

    try {
      await meetingSessionRef.current.audioVideo.chooseAudioOutput(
        device.deviceId
      );
      setSelectedAudioOutput(device);
      saveDevicePreference(STORAGE_KEYS.audioOutput, device.deviceId);
      toast.success(`Switched to speaker: ${device.label || "Default"}`);
    } catch (error) {
      console.error("Failed to change audio output device:", error);
      toast.error("Speaker selection not supported on this browser");
    }
  };

  const isMobileDevice = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  const isIOSDevice = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const enableAudioPlayback = async () => {
    const audioElement = audioElementRef.current;
    const meetingSession = meetingSessionRef.current;

    if (!audioElement || !meetingSession) {
      console.warn("Audio element or meeting session not available");
      return;
    }

    try {
      await meetingSession.audioVideo.bindAudioElement(audioElement);

      audioElement.muted = false;
      audioElement.volume = 1.0;

      await audioElement.play();

      setNeedsAudioUnmute(false);
      toast.success("Audio enabled");

      setAudioElementState({
        hasStream: !!audioElement.srcObject,
        isPlaying: !audioElement.paused,
        currentTime: audioElement.currentTime,
        readyState: audioElement.readyState,
      });
    } catch (error) {
      console.error("❌ Failed to enable audio:", error);

      try {
        const silentAudio = new Audio(
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleDs/"
        );
        silentAudio.volume = 0.01;
        await silentAudio.play();
        silentAudio.pause();

        await audioElement.play();
        setNeedsAudioUnmute(false);
        toast.success("Audio enabled");

        setAudioElementState({
          hasStream: !!audioElement.srcObject,
          isPlaying: !audioElement.paused,
          currentTime: audioElement.currentTime,
          readyState: audioElement.readyState,
        });
      } catch (retryError) {
        console.error("❌ Retry failed:", retryError);
        toast.error("Tap again to enable audio");
      }
    }
  };

  const initializeAudioDevices = async (meetingSession) => {
    try {
      const isMobile = isMobileDevice();
      const isIOS = isIOSDevice();

      const audioInputDevices =
        await meetingSession.audioVideo.listAudioInputDevices();

      if (audioInputDevices.length === 0) {
        throw new Error(
          "No microphone devices found. Please connect a microphone and refresh the page."
        );
      }

      const audioOutputDevices =
        await meetingSession.audioVideo.listAudioOutputDevices();

      const audioElement = audioElementRef.current;
      if (audioElement) {
        audioElement.autoplay = true;
        audioElement.playsInline = true;
        audioElement.muted = false;
        audioElement.volume = 1.0;

        await meetingSession.audioVideo.bindAudioElement(audioElement);

        if (isMobile) {
          try {
            await audioElement.play();

            setNeedsAudioUnmute(false);
          } catch (playError) {
            setNeedsAudioUnmute(true);
          }
        }
      }

      if (isMobile && audioInputDevices.length > 0) {
        const defaultMic = audioInputDevices[0];
        await meetingSession.audioVideo.startAudioInput(defaultMic.deviceId);
        setSelectedAudioInput(defaultMic);
      } else {
        try {
          await meetingSession.audioVideo.startAudioInput(null);
        } catch (nullError) {
          if (audioInputDevices.length > 0) {
            await meetingSession.audioVideo.startAudioInput(
              audioInputDevices[0].deviceId
            );
            setSelectedAudioInput(audioInputDevices[0]);
          }
        }
      }

      if (!isIOS && audioOutputDevices.length > 0) {
        try {
          await meetingSession.audioVideo.chooseAudioOutput(
            audioOutputDevices[0].deviceId
          );
          setSelectedAudioOutput(audioOutputDevices[0]);
        } catch (outputError) {}
      } else {
      }

      setAvailableAudioInputs(audioInputDevices);
      setAvailableAudioOutputs(audioOutputDevices);

      toast.success("Audio connected");
      return true;
    } catch (error) {
      console.error("Audio device initialization failed:", error);

      if (
        error.message.includes("Permission denied") ||
        error.name === "NotAllowedError"
      ) {
        throw new Error(
          "Audio permission denied. Please allow microphone access and refresh the page."
        );
      } else if (
        error.message.includes("Could not start audio input") ||
        error.message.includes("startAudioInput")
      ) {
        throw new Error(
          "Failed to start microphone. Please check your audio settings and ensure no other application is using your microphone."
        );
      } else {
        throw new Error(`Audio initialization failed: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    const initializeChimeMeeting = async () => {
      const meetingId = searchParams.get("meetingId");

      if (!meetingId) {
        setError("No meeting ID provided");
        toast.error("No meeting ID provided");
        setIsLoading(false);
        return;
      }

      try {
        const credentialsStr = localStorage.getItem("videoCallCredentials");

        if (!credentialsStr) {
          setError(
            "No meeting credentials found. Please join the video call from the appointment page."
          );
          toast.error("Please join from the appointment page");
          setIsLoading(false);
          return;
        }

        const credentials = JSON.parse(credentialsStr);

        const meeting = credentials.Meeting ||
          credentials.meeting || {
            MeetingId: credentials.meetingId,
            ExternalMeetingId: credentials.externalMeetingId,
            MediaRegion: credentials.mediaRegion,
            MediaPlacement: credentials.MediaPlacement ||
              credentials.mediaPlacement || {
                AudioHostUrl: credentials.audioHostUrl,
                AudioFallbackUrl: credentials.audioFallbackUrl,
                SignalingUrl: credentials.signalingUrl,
                TurnControlUrl: credentials.turnControlUrl,
                ScreenDataUrl: credentials.screenDataUrl,
                ScreenSharingUrl: credentials.screenSharingUrl,
                ScreenViewingUrl: credentials.screenViewingUrl,
              },
          };

        const attendee = credentials.Attendee ||
          credentials.attendee || {
            AttendeeId: credentials.attendeeId,
            ExternalUserId: credentials.externalUserId,
            JoinToken: credentials.joinToken,
          };

        const currentMeetingId = meeting.MeetingId || credentials.meetingId;
        const currentAttendeeId = attendee.AttendeeId || credentials.attendeeId;
        const currentJoinToken = attendee.JoinToken || credentials.joinToken;

        if (!currentMeetingId) {
          setError("Missing meeting ID in credentials");
          toast.error("Invalid credentials: Missing meeting ID");
          setIsLoading(false);
          return;
        }

        if (!currentAttendeeId || !currentJoinToken) {
          setError("Missing attendee credentials");
          toast.error("Invalid credentials: Missing attendee information");
          setIsLoading(false);
          return;
        }

        const mediaPlacement = meeting.MediaPlacement;

        if (!mediaPlacement?.AudioHostUrl) {
          const errMsg = "Missing AudioHostUrl - audio will not work";
          console.error("❌", errMsg);
          toast.error(errMsg);
          setError(errMsg);
          setIsLoading(false);
          return;
        }

        if (!mediaPlacement?.SignalingUrl) {
          const errMsg = "Missing SignalingUrl - connection will fail";
          console.error("❌", errMsg);
          toast.error(errMsg);
          setError(errMsg);
          setIsLoading(false);
          return;
        }

        if (currentMeetingId !== meetingId) {
          setError(
            `Meeting ID mismatch: Expected ${meetingId}, got ${currentMeetingId}`
          );
          toast.error("Meeting ID mismatch");
          setIsLoading(false);
          return;
        }

        // Check WebRTC support first
        const webRTCSupport = checkWebRTCSupport();
        if (!webRTCSupport.isSupported) {
          setError(
            `Your browser is missing required features: ${webRTCSupport.missingFeatures.join(
              ", "
            )}. Please use a modern browser like Chrome, Firefox, or Safari.`
          );
          toast.error("Browser not supported");
          setIsLoading(false);
          return;
        }

        const logger = new ConsoleLogger("ChimeMeeting", LogLevel.INFO);

        const deviceController = new DefaultDeviceController(logger);

        const configuration = new MeetingSessionConfiguration(
          meeting,
          attendee
        );

        const meetingSession = new DefaultMeetingSession(
          configuration,
          logger,
          deviceController
        );

        meetingSessionRef.current = meetingSession;

        // Initialize resilience monitoring
        // Initialize resilience monitoring with meeting info for backend API calls
        await setupMeetingResilience(meetingSession, audioElementRef.current, {
          meetingId: currentMeetingId,
          attendeeId: currentAttendeeId,
          externalUserId: attendee.ExternalUserId || credentials.externalUserId,
        });

        // Create resilient observer with video tile handling. The
        // observer can fire before React has mounted the <video>
        // element it needs to bind to (new-tab open race), so we
        // retry until the ref attaches instead of handing Chime a
        // null element (which throws inside bindVideoElement).
        const bindWhenReady = (tileId, elementRef, label) => {
          const tryBind = (attempt = 0) => {
            if (elementRef.current) {
              meetingSession.audioVideo.bindVideoElement(
                tileId,
                elementRef.current
              );
            } else if (attempt < 10) {
              setTimeout(() => tryBind(attempt + 1), 200);
            } else {
              console.warn(
                `Failed to bind ${label} video after ${attempt} attempts`
              );
            }
          };
          tryBind();
        };

        const resilientObserver = createResilientObserver({
          videoTileDidUpdate: (tileState) => {
            if (!tileState.boundAttendeeId) {
              return;
            }

            if (tileState.localTile) {
              bindWhenReady(tileState.tileId, videoElementRef, "local");
            } else {
              bindWhenReady(tileState.tileId, remoteVideoElementRef, "remote");
            }
          },
          videoTileWasRemoved: (_tileId) => {},
        });

        const addAudioVideoObserver = (observer) => {
          meetingSession.audioVideo.addObserver(observer);
          audioVideoObserversRef.current.push(observer);
        };

        addAudioVideoObserver(resilientObserver);

        // Audio level observer for volume indicators
        const audioLevelObserver = {
          audioVideoDidStart: async () => {
            // Use the resilient audio binding
            try {
              const result = await bindAudioWithResilience(
                meetingSession,
                audioElementRef.current
              );

              if (result.needsUserInteraction) {
                setNeedsAudioUnmute(true);
              } else {
                setNeedsAudioUnmute(false);
              }

              setAudioElementState({
                hasStream: !!audioElementRef.current?.srcObject,
                isPlaying: !audioElementRef.current?.paused,
                currentTime: audioElementRef.current?.currentTime || 0,
                readyState: audioElementRef.current?.readyState || 0,
              });
            } catch (error) {
              console.error("Audio binding failed:", error);
              showToastOnce(
                "speaker-not-connected",
                "Speaker audio not connected - you may not hear other participants",
                "error"
              );
            }
          },
        };

        addAudioVideoObserver(audioLevelObserver);

        meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
          meetingSession.configuration.credentials.attendeeId,
          (attendeeId, volume, muted, _signalStrength) => {
            if (!muted && volume !== null) {
              const volumePercentage = Math.round(volume * 100);
              setMicrophoneLevel(volumePercentage);
            } else {
              setMicrophoneLevel(0);
            }
          }
        );

        meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
          (attendeeId, present) => {
            if (
              present &&
              attendeeId !== meetingSession.configuration.credentials.attendeeId
            ) {
              meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
                attendeeId,
                (remoteAttendeeId, volume, muted, _signalStrength) => {
                  setRemoteParticipantMuted(muted);

                  setRemoteSpeakerLevels((prev) => {
                    if (!muted && volume !== null && volume > 0) {
                      const volumePercentage = Math.round(volume * 100);
                      return { ...prev, [remoteAttendeeId]: volumePercentage };
                    } else {
                      const updated = { ...prev };
                      delete updated[remoteAttendeeId];
                      return updated;
                    }
                  });
                }
              );
            } else if (
              !present &&
              attendeeId !== meetingSession.configuration.credentials.attendeeId
            ) {
              handleRemoteAttendeeLeft();
              setRemoteParticipantMuted(false);
              setRemoteSpeakerLevels((prev) => {
                const updated = { ...prev };
                delete updated[attendeeId];
                return updated;
              });
            }
          }
        );

        const videoInputDevices =
          await meetingSession.audioVideo.listVideoInputDevices();

        if (videoInputDevices.length > 0) {
          try {
            const savedVideoId = getSavedDevicePreference(
              STORAGE_KEYS.videoInput
            );
            const savedVideoDevice = findSavedDevice(
              videoInputDevices,
              savedVideoId
            );
            const videoDeviceToUse = savedVideoDevice || videoInputDevices[0];

            await meetingSession.audioVideo.startVideoInput(
              videoDeviceToUse.deviceId
            );
            setSelectedVideoInput(videoDeviceToUse);

            if (savedVideoDevice) {
            } else {
            }
          } catch (error) {
            console.warn("Failed to initialize video device:", error);
            toast.warn(
              "Camera initialization failed, but you can still join with audio only"
            );
          }
        } else {
          console.warn("No video input devices found");
          toast.warn("No camera found, joining with audio only");
        }

        meetingSession.audioVideo.start();

        meetingSession.audioVideo.startLocalVideoTile();

        try {
          await checkMicrophonePermissions();

          await initializeAudioDevices(meetingSession);

          await loadAvailableDevices(meetingSession);

          verifyAudioTransmission(meetingSession);
        } catch (audioError) {
          console.error("❌ Audio initialization failed:", audioError);
          toast.error(`Audio setup failed: ${audioError.message}`);
        }

        setIsConnected(true);
        setIsLoading(false);
        toast.success("Connected to meeting");
      } catch (err) {
        console.error("Error initializing Chime meeting:", err);
        setError(err.message || "Failed to join meeting");
        toast.error("Failed to join meeting");
        setIsLoading(false);
      }
    };

    initializeChimeMeeting();

    const handleBeforeUnload = () => {
      cleanupMeeting();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      cleanupMeeting();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount
  }, [searchParams]);

  const handleToggleMute = () => {
    if (!meetingSessionRef.current) return;

    if (isMuted) {
      meetingSessionRef.current.audioVideo.realtimeUnmuteLocalAudio();
      setIsMuted(false);
      toast.success("Microphone unmuted");
    } else {
      meetingSessionRef.current.audioVideo.realtimeMuteLocalAudio();
      setIsMuted(true);
      setMicrophoneLevel(0);
      toast.success("Microphone muted");
    }
  };

  const handleToggleVideo = async () => {
    if (!meetingSessionRef.current) return;

    try {
      if (isVideoOff) {
        const savedVideoId = getSavedDevicePreference(STORAGE_KEYS.videoInput);
        let deviceToUse = null;

        if (savedVideoId && availableVideoInputs.length > 0) {
          deviceToUse = availableVideoInputs.find(
            (d) => d.deviceId === savedVideoId
          );
        }
        if (!deviceToUse && availableVideoInputs.length > 0) {
          deviceToUse = selectedVideoInput || availableVideoInputs[0];
        }

        if (deviceToUse) {
          await meetingSessionRef.current.audioVideo.startVideoInput(
            deviceToUse.deviceId
          );
          setSelectedVideoInput(deviceToUse);
        } else {
          await meetingSessionRef.current.audioVideo.startVideoInput(null);
        }

        meetingSessionRef.current.audioVideo.startLocalVideoTile();
        setIsVideoOff(false);

        toast.success("Video turned on");
      } else {
        meetingSessionRef.current.audioVideo.stopLocalVideoTile();
        await meetingSessionRef.current.audioVideo.stopVideoInput();
        setIsVideoOff(true);

        toast.success("Video turned off");
      }
    } catch (error) {
      console.error("❌ Failed to toggle video:", error);
      toast.error("Failed to toggle camera");

      try {
        if (!isVideoOff) {
          meetingSessionRef.current.audioVideo.stopLocalVideoTile();
          setIsVideoOff(true);
        }
      } catch (e) {
        console.error("Recovery failed:", e);
      }
    }
  };

  const handleLeaveMeeting = async () => {
    await cleanupMeeting();
    toast.success("Left the meeting");
    window.close();
  };

  if (isLoading) {
    return (
      <div className={styles["video-call-container"]}>
        <div className={styles["video-call-loading"]}>
          <div className={styles.spinner}></div>
          <p>Connecting to video call...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles["video-call-container"]}>
        <div className={styles["video-call-error"]}>
          <h2>Unable to join meeting</h2>
          <p>{error}</p>
          <button
            onClick={() => window.close()}
            className={styles["btn-close-window"]}
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["video-call-container"]}>
      <audio
        ref={audioElementRef}
        autoPlay
        playsInline
        style={{ display: "none" }}
      />

      <div className={styles["video-grid"]}>
        <div className={styles["video-main"]}>
          <video
            ref={remoteVideoElementRef}
            className={styles["remote-video"]}
            autoPlay
            playsInline
          />
          {!isConnected && (
            <div className={styles["video-placeholder"]}>
              <p>Waiting for other participants...</p>
            </div>
          )}

          {isConnected && remoteParticipantMuted && (
            <div
              style={{
                position: "absolute",
                top: "8px",
                left: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(239, 68, 68, 0.9)",
                padding: "6px 12px",
                borderRadius: "16px",
                zIndex: 15,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
              <span
                style={{
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                Participant Muted
              </span>
            </div>
          )}

          {(needsAudioUnmute ||
            (isConnected &&
              audioElementState.hasStream &&
              !audioElementState.isPlaying)) && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 100,
              }}
            >
              <button
                onClick={enableAudioPlayback}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                  background: "rgba(34, 197, 94, 0.95)",
                  border: "none",
                  borderRadius: "16px",
                  padding: "24px 32px",
                  cursor: "pointer",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                  animation: "pulse 2s infinite",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                </svg>
                <span
                  style={{
                    color: "white",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Tap to Enable Audio
                </span>
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "13px",
                  }}
                >
                  Audio requires interaction on this device
                </span>
              </button>
            </div>
          )}

          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(0, 0, 0, 0.8)",
                padding: "6px 10px",
                borderRadius: "6px",
                backdropFilter: "blur(10px)",
                fontSize: "11px",
                color: "#fff",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: audioElementState.hasStream
                    ? "#4ade80"
                    : "#ef4444",
                  boxShadow: audioElementState.hasStream
                    ? "0 0 6px #4ade80"
                    : "0 0 6px #ef4444",
                }}
              />
              <span>
                Stream:{" "}
                {audioElementState.hasStream ? "Connected" : "No Stream"}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(0, 0, 0, 0.8)",
                padding: "6px 10px",
                borderRadius: "6px",
                backdropFilter: "blur(10px)",
                fontSize: "11px",
                color: "#fff",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: audioElementState.isPlaying
                    ? "#4ade80"
                    : "#fbbf24",
                  boxShadow: audioElementState.isPlaying
                    ? "0 0 6px #4ade80"
                    : "0 0 6px #fbbf24",
                }}
              />
              <span>Playing: {audioElementState.isPlaying ? "Yes" : "No"}</span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(0, 0, 0, 0.8)",
                padding: "6px 10px",
                borderRadius: "6px",
                backdropFilter: "blur(10px)",
                fontSize: "11px",
                color: "#fff",
              }}
            >
              <span>Ready: {audioElementState.readyState}/4</span>
            </div>

            {(!audioElementState.hasStream || !audioElementState.isPlaying) &&
              isConnected && (
                <button
                  onClick={async () => {
                    toast.dismiss("audio-retry");
                    toast.loading("Retrying audio...", { id: "audio-retry" });
                    const audioElement = audioElementRef.current;
                    const meetingSession = meetingSessionRef.current;

                    if (audioElement && meetingSession) {
                      try {
                        try {
                          const audioContext = new (window.AudioContext ||
                            window.webkitAudioContext)();
                          if (audioContext.state === "suspended") {
                            await audioContext.resume();
                          }
                          const oscillator = audioContext.createOscillator();
                          const gainNode = audioContext.createGain();
                          gainNode.gain.value = 0.001;
                          oscillator.connect(gainNode);
                          gainNode.connect(audioContext.destination);
                          oscillator.start();
                          oscillator.stop(audioContext.currentTime + 0.1);
                          await new Promise((resolve) =>
                            setTimeout(resolve, 100)
                          );
                          audioContext.close();
                        } catch (ctxErr) {}

                        audioElement.autoplay = true;
                        audioElement.playsInline = true;
                        audioElement.muted = false;
                        audioElement.volume = 1.0;

                        await meetingSession.audioVideo.bindAudioElement(
                          audioElement
                        );

                        let playSuccess = false;
                        try {
                          await audioElement.play();

                          playSuccess = true;
                          setNeedsAudioUnmute(false);
                        } catch (playErr) {}

                        setAudioElementState({
                          hasStream: !!audioElement.srcObject,
                          isPlaying: !audioElement.paused,
                          currentTime: audioElement.currentTime,
                          readyState: audioElement.readyState,
                        });

                        toast.dismiss("audio-retry");
                        if (playSuccess && !audioElement.paused) {
                          toast.success("Audio enabled!", {
                            id: "audio-enabled",
                          });
                        } else if (audioElement.srcObject) {
                          toast.error("Tap again to enable audio", {
                            id: "tap-again",
                          });
                          setNeedsAudioUnmute(true);
                        } else {
                          toast.error(
                            "No audio - waiting for other participant",
                            { id: "no-audio" }
                          );
                        }
                      } catch (err) {
                        console.error("❌ Audio retry failed:", err);
                        toast.dismiss("audio-retry");
                        toast.error("Audio retry failed", {
                          id: "retry-failed",
                        });
                      }
                    }
                  }}
                  style={{
                    background: !audioElementState.isPlaying
                      ? "rgba(251, 191, 36, 0.9)"
                      : "rgba(239, 68, 68, 0.9)",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    fontSize: "11px",
                    color: "#000",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  {!audioElementState.hasStream
                    ? "Retry Audio"
                    : "▶ Play Audio"}
                </button>
              )}
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "12px",
              left: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              background: "rgba(0, 0, 0, 0.8)",
              padding: "10px 12px",
              borderRadius: "8px",
              backdropFilter: "blur(10px)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={
                  remoteAudioActive ||
                  Object.keys(remoteSpeakerLevels).length > 0
                    ? "#4ade80"
                    : "#6b7280"
                }
                strokeWidth="2"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
              </svg>
              <span
                style={{
                  fontSize: "11px",
                  color:
                    remoteAudioActive ||
                    Object.keys(remoteSpeakerLevels).length > 0
                      ? "#4ade80"
                      : "#6b7280",
                  fontWeight: "500",
                }}
              >
                {remoteAudioActive ||
                Object.keys(remoteSpeakerLevels).length > 0
                  ? "Receiving Audio"
                  : "No Audio"}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "2px",
                height: "32px",
                padding: "4px 0",
              }}
            >
              {audioVisualizerData.slice(0, 16).map((value, i) => (
                <div
                  key={i}
                  style={{
                    width: "4px",
                    height: `${Math.max(4, value * 0.32)}px`,
                    background:
                      value > 50
                        ? "#ef4444"
                        : value > 25
                        ? "#fbbf24"
                        : value > 5
                        ? "#4ade80"
                        : "rgba(255, 255, 255, 0.2)",
                    borderRadius: "2px",
                    transition: "height 0.05s ease, background 0.1s ease",
                  }}
                />
              ))}
            </div>

            {Object.keys(remoteSpeakerLevels).length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                }}
              >
                <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                  SDK Level:
                </span>
                <div
                  style={{
                    flex: 1,
                    height: "4px",
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "2px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(
                        ...Object.values(remoteSpeakerLevels),
                        0
                      )}%`,
                      height: "100%",
                      background: "#4ade80",
                      borderRadius: "2px",
                      transition: "width 0.1s ease",
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#9ca3af",
                    minWidth: "30px",
                  }}
                >
                  {Math.max(...Object.values(remoteSpeakerLevels), 0)}%
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={styles["video-local"]}>
          <video
            ref={videoElementRef}
            className={styles["local-video"]}
            autoPlay
            playsInline
            muted
          />
          {isVideoOff && (
            <div className={styles["video-off-placeholder"]}>
              <p>Camera Off</p>
            </div>
          )}

          {!isMuted && microphoneLevel > 5 && (
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "8px",
                right: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(0, 0, 0, 0.7)",
                padding: "6px 8px",
                borderRadius: "6px",
                backdropFilter: "blur(10px)",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4ade80"
                strokeWidth="2"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
              <div
                style={{
                  flex: 1,
                  height: "4px",
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${microphoneLevel}%`,
                    height: "100%",
                    background:
                      microphoneLevel > 70
                        ? "#ef4444"
                        : microphoneLevel > 40
                        ? "#fbbf24"
                        : "#4ade80",
                    borderRadius: "2px",
                    transition: "width 0.1s ease",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles["video-controls"]}>
        <button
          onClick={handleToggleMute}
          className={`${styles["control-btn"]} ${isMuted ? styles.active : ""}`}
          title={isMuted ? "Unmute" : "Mute"}
          style={{ position: "relative" }}
        >
          {!isMuted && microphoneLevel > 10 && (
            <div
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#4ade80",
                border: "2px solid white",
                animation: microphoneLevel > 30 ? "pulse 1s infinite" : "none",
              }}
            />
          )}
          {isMuted ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          )}
        </button>

        <button
          onClick={handleToggleVideo}
          className={`${styles["control-btn"]} ${
            isVideoOff ? styles.active : ""
          }`}
          title={isVideoOff ? "Turn on camera" : "Turn off camera"}
        >
          {isVideoOff ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M10.66 5H14l3 3v8"></path>
              <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2l10 10z"></path>
              <path d="M23 7l-7 5 7 5V7z"></path>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          )}
        </button>

        <button
          onClick={() => setShowDeviceSettings(true)}
          className={styles["control-btn"]}
          title="Device settings"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <button
          onClick={handleLeaveMeeting}
          className={`${styles["control-btn"]} ${styles["leave-btn"]}`}
          title="Leave meeting"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
            <line x1="12" y1="2" x2="12" y2="12"></line>
          </svg>
        </button>
      </div>

      <div className={styles["meeting-info"]}>
        <p>Meeting ID: {searchParams.get("meetingId")}</p>
      </div>

      {showDeviceSettings && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "500px",
              color: "#ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                Device Settings
              </h3>
              <button
                onClick={() => setShowDeviceSettings(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Microphone
              </label>
              <select
                value={selectedAudioInput?.deviceId || ""}
                onChange={(e) => {
                  const device = availableAudioInputs.find(
                    (d) => d.deviceId === e.target.value
                  );
                  if (device) changeAudioInputDevice(device);
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #374151",
                  backgroundColor: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                }}
              >
                {availableAudioInputs.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(-4)}`}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Camera
              </label>
              <select
                value={selectedVideoInput?.deviceId || ""}
                onChange={(e) => {
                  const device = availableVideoInputs.find(
                    (d) => d.deviceId === e.target.value
                  );
                  if (device) changeVideoInputDevice(device);
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #374151",
                  backgroundColor: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                }}
              >
                {availableVideoInputs.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Camera ${device.deviceId.slice(-4)}`}
                  </option>
                ))}
              </select>
            </div>

            {!/iPhone|iPad|iPod/i.test(navigator.userAgent) &&
              availableAudioOutputs.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Speaker
                  </label>
                  <select
                    value={selectedAudioOutput?.deviceId || ""}
                    onChange={(e) => {
                      const device = availableAudioOutputs.find(
                        (d) => d.deviceId === e.target.value
                      );
                      if (device) changeAudioOutputDevice(device);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #374151",
                      backgroundColor: "#111827",
                      color: "#ffffff",
                      fontSize: "14px",
                    }}
                  >
                    {availableAudioOutputs.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Speaker ${device.deviceId.slice(-4)}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/iPhone|iPad|iPod/i.test(navigator.userAgent) && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "12px",
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "6px",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#93c5fd",
                  }}
                >
                  Speaker selection is managed by your device. Use your iPhone's
                  volume controls or connect headphones/AirPods.
                </p>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowDeviceSettings(false)}
                style={{
                  backgroundColor: "#059669",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { VideoCallPage };
